/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    FilterButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import { useDeleteUserRestrictions } from "../../hooks/client-chart-restriction.mutations";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchRestrictedUsersList } from "../../hooks/client-chart-restriction.queries";
import { IRestrictedUserSingleClient } from "../../types/client-chart-restriction.types";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    truncateString,
} from "../../../../utils/helpers";
import AddRestrictionForm from "../AddRestrictionForm/AddRestrictionForm";
import { useFetchUsersList } from "../../../user-management/hooks/user-management.queries";
import ListState from "../../../../components/ListState/ListState";
import CheckPrimaryColorIcon from "../../../../components/Icons/CheckPrimaryColor";
import MobileListItem from "../../../../shared-ui/MobileListItem/MobileListItem";
import DeleteIcon from "../../../../icons/Delete";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useDoesUserHaveRole } from "../../../../hooks/permissions";
import { UserRole } from "../../../../utils/types/user";

type Dialogs = "add_restriction" | "delete_restriction";

export default function RestrictedUsersList() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [showInternalUsersPopover, setShowInternalUsersPopover] =
        React.useState(false);
    const [internalUsersSearchString, setInternalUsersSearchString] =
        React.useState("");
    const [selectedIds, setSelectedIds] = React.useState<
        { restrictionId: string; clientIds: string[] }[]
    >([]);
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [internalUserRowSelection, setInternalUserRowSelection] =
        React.useState({});
    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "",
    });

    const queryClient = useQueryClient();
    const { toast } = useToast();

    const canWriteRestrictions = useDoesUserHaveRole([
        UserRole.SUPER_ADMIN,
        UserRole.BILLER_ADMIN,
    ]);

    const deleteUserRestrictions = useDeleteUserRestrictions();

    // To determine if mutation is currently running, returns 0 || 1
    const restrictAccessIsMutating = useIsMutating(["restrict-access"]);

    // get filters from params
    const clientNameFilter = searchParams.get("client") || "";
    const userNameFilter = searchParams.get("user") || "";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");

    const debouncedClientNameFilter = useDebounce(clientNameFilter);
    const debouncedUserNameFilter = useDebounce(
        internalUsersSearchString || ""
    );

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const { data, isLoading, isSuccess, error } = useFetchRestrictedUsersList({
        user_search_string: debouncedUserNameFilter,
        client_search_string: debouncedClientNameFilter,
        from_date: fromDateFilter,
        to_date: toDateFilter,
    });

    const internalUsersList = useFetchUsersList({
        page: 1,
        limit: 20,
        search_string: debouncedUserNameFilter,
    });

    const handleUserNameFilterChange = (userName: string) => {
        if (!userName) searchParams.delete("user");
        else searchParams.set("user", userName);

        setSearchParams(searchParams);
    };

    const handleClientNameFilterChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("client");
        else searchParams.set("client", e.target.value);

        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));

        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));

        setSearchParams(searchParams);
    };

    // The current API response returns the associated clients for a restricted user in an array, but the Design requires that every client - user relationship is a separate row. This means we have to create a new row for each client in a restricted user's client list
    const openRestrictionList: IRestrictedUserSingleClient[] = [];
    data?.data.forEach((item) => {
        item?.clients?.forEach((client) => {
            openRestrictionList.push({ ...item, client });
        });

        // fill up the selectedIds array based on the available restriction ids
        if (
            !selectedIds.find(
                (singleId) => singleId.restrictionId === item.restriction_id
            )
        ) {
            selectedIds.push({
                restrictionId: item.restriction_id,
                clientIds: [],
            });
        }
    });

    const handleDialogInView = (modal: Dialogs | null) => {
        setDialogInView(() => modal);
    };

    // total selected ids
    const totalSelectedIds = selectedIds.reduce(
        (acc, item) => acc + item.clientIds.length,
        0
    );

    // Toggles user selection for mobile
    const toggleItemSelection = (restrictionId: string, clientId: string) => {
        const selectedRestriction = selectedIds.find(
            (singleId) => singleId.restrictionId === restrictionId
        );

        if (selectedRestriction) {
            if (selectedRestriction.clientIds.includes(clientId)) {
                const newList = selectedRestriction?.clientIds.filter(
                    (id) => id !== clientId
                );

                selectedRestriction.clientIds = newList;
            } else {
                selectedRestriction.clientIds = [
                    ...selectedRestriction.clientIds,
                    clientId,
                ];
            }
            setSelectedIds((prev) => [
                ...prev.filter((item) => item.restrictionId !== restrictionId),
                selectedRestriction,
            ]);
        }
    };

    const handleDeleteRestriction = () => {
        const parsedSelectedIds = selectedIds.map((item) => ({
            [item.restrictionId]: [...item.clientIds],
        }));

        deleteUserRestrictions.mutate(parsedSelectedIds, {
            onSuccess: async (res) => {
                queryClient.invalidateQueries(["restricted-users"]);
                toast({
                    mode: "success",
                    message:
                        res.message || "Restriction(s) removed successfully",
                });
                setInternalUserRowSelection({});
                setSelectedIds([]);
                setDialogInView(null);
            },
            onError(err) {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not remove restriction(s), please try again",
                });
            },
        });
    };

    const columns: ColumnDef<IRestrictedUserSingleClient>[] = [
        {
            id: "select",
            header: "",
            cell: ({ row }) =>
                canWriteRestrictions ? (
                    <Checkbox
                        handleChange={() =>
                            toggleItemSelection(
                                row.original.restriction_id,
                                row.original.client.client_id
                            )
                        }
                        indeterminate={row.getIsSomeSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={selectedIds
                            .find(
                                (singleId) =>
                                    singleId.restrictionId ===
                                    row.original.restriction_id
                            )
                            ?.clientIds.includes(row.original.client.client_id)}
                    />
                ) : null,
        },
        {
            accessorKey: "restricted_user",
            header: "RESTRICTED USER",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.restricted_user.first_name,
                    lastName: row.original.restricted_user.last_name,
                }),
        },
        {
            accessorKey: "client",
            header: "CLIENT NAME",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.client.first_name,
                    lastName: row.original.client.last_name,
                }),
        },
        {
            accessorKey: "date_restricted",
            header: "DATE RESTRICTED",
            cell: ({ row }) => formatDate(row.original.date_restricted),
        },
        {
            accessorKey: "restricted_by",
            header: "RESTRICTED BY",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.restricted_by.first_name,
                    lastName: row.original.restricted_by.last_name,
                }),
        },
        {
            accessorKey: "restriction_id",
            header: "",
            cell: ({ row }) =>
                canWriteRestrictions ? (
                    <Button
                        type="button"
                        variant="can-hover"
                        aria-label="delete button"
                        onClick={() => {
                            toggleItemSelection(
                                row.original.restriction_id,
                                row.original.client.client_id
                            );
                            handleDialogInView("delete_restriction");
                        }}
                    >
                        <DeleteIcon />
                    </Button>
                ) : null,
        },
    ];

    return (
        <>
            <AlertDialog open={dialogInView === "delete_restriction"}>
                <AlertDialogContent
                    isDeleting
                    onCancelClick={() => handleDialogInView(null)}
                    title=" Delete restriction(s)"
                    description=" Are you sure you want to delete the selected restriction(s)?"
                    saveText="Delete"
                    cancelText="Cancel"
                    isCancelBtnDisabled={deleteUserRestrictions.isLoading}
                    isSaveBtnDisabled={deleteUserRestrictions.isLoading}
                    onSaveClick={handleDeleteRestriction}
                />
            </AlertDialog>
            <Dialog open={dialogInView === "add_restriction"}>
                <DialogContent
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    title="Add restriction"
                    showFooter
                    saveText="Add restriction"
                    cancelText="Cancel"
                    submitBtnFormValue="add-restriction-form"
                    isCancelBtnDisabled={restrictAccessIsMutating > 0}
                    isSubmitBtnDisabled={restrictAccessIsMutating > 0}
                >
                    <AddRestrictionForm
                        onFormSubmit={() => handleDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>

            <Card classNames="mb-[--spacing-header]">
                <div className="flex flex-col gap-[--spacing-items]">
                    <SearchInput
                        defaultValue={clientNameFilter}
                        onChange={handleClientNameFilterChange}
                        containerClass="w-[400px]"
                        placeholder="Search by client name"
                    />
                    <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                        <div className="flex items-center flex-wrap gap-[--spacing-items]">
                            <Popover
                                open={showInternalUsersPopover}
                                onOpenChange={(state) => {
                                    setShowInternalUsersPopover(state);
                                }}
                            >
                                <PopoverTrigger
                                    asChild
                                    onClick={() =>
                                        setShowInternalUsersPopover(true)
                                    }
                                >
                                    <FilterButton
                                        className={
                                            userNameFilter
                                                ? "capitalize"
                                                : "normal-case"
                                        }
                                    >
                                        {userNameFilter
                                            ? truncateString(userNameFilter)
                                            : "User"}
                                    </FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="start">
                                    <SearchInput
                                        placeholder="Search by name"
                                        onChange={(e) =>
                                            setInternalUsersSearchString(
                                                e.target.value
                                            )
                                        }
                                    />
                                    <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                        {internalUsersList.isLoading && (
                                            <Loader wrapperClassName="h-[150px]" />
                                        )}
                                        {internalUsersList.error && (
                                            <ListState
                                                isError
                                                context="general"
                                                stateHelperText="Please try again later"
                                                errorMsg={
                                                    internalUsersList.error
                                                        ?.response?.data
                                                        ?.message ||
                                                    "Error loading internal users"
                                                }
                                                cardClass="h-[150px]"
                                            />
                                        )}
                                        {internalUsersList.data?.data &&
                                            internalUsersList.isSuccess && (
                                                <>
                                                    <Button
                                                        size="auto"
                                                        variant="can-hover"
                                                        className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                        onClick={() => {
                                                            handleUserNameFilterChange(
                                                                ""
                                                            );
                                                            setShowInternalUsersPopover(
                                                                false
                                                            );
                                                            setInternalUsersSearchString(
                                                                ""
                                                            );
                                                        }}
                                                    >
                                                        All
                                                    </Button>
                                                    {internalUsersList?.data?.data.map(
                                                        (user) => (
                                                            <Button
                                                                key={
                                                                    user.user_id
                                                                }
                                                                size="auto"
                                                                variant="can-hover"
                                                                className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                                onClick={() => {
                                                                    handleUserNameFilterChange(
                                                                        `${user.first_name} ${user.last_name}`
                                                                    );
                                                                    setShowInternalUsersPopover(
                                                                        false
                                                                    );
                                                                }}
                                                            >
                                                                {`${user.first_name} ${user.last_name}`}
                                                                {userNameFilter.toLocaleLowerCase() ===
                                                                `${user.first_name} ${user.last_name}`.toLowerCase() ? (
                                                                    <CheckPrimaryColorIcon />
                                                                ) : null}
                                                            </Button>
                                                        )
                                                    )}
                                                </>
                                            )}
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>
                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        {canWriteRestrictions && (
                            <div>
                                <Button
                                    onClick={() =>
                                        handleDialogInView("add_restriction")
                                    }
                                >
                                    Add restriction
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {isLoading ? <Loader /> : null}

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display restricted users list at this time please try again later`
                    }
                />
            )}

            {openRestrictionList &&
                isSuccess &&
                openRestrictionList.length === 0 && (
                    <ListState
                        stateHelperText="Restricted users list will appear here once they are added"
                        isError={false}
                        emptyMessage="No restricted user added yet"
                    />
                )}

            {isSuccess &&
            openRestrictionList &&
            openRestrictionList.length > 0 ? (
                <div>
                    {totalSelectedIds > 0 ? (
                        <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                            <div className="font-semibold text-base">
                                {`${totalSelectedIds} selected`}:
                            </div>
                            <Button
                                variant="secondary"
                                aria-label="actions"
                                className="bg-danger text-white hover:bg-danger"
                                onClick={() =>
                                    handleDialogInView("delete_restriction")
                                }
                            >
                                {getTotalAmountOfTableRowsSelected(
                                    internalUserRowSelection
                                ).amount > 1
                                    ? "Delete restrictions"
                                    : "Delete restriction"}
                            </Button>
                        </div>
                    ) : null}

                    <div className="hidden md:block">
                        <Table
                            columns={columns}
                            data={openRestrictionList}
                            rowSelection={internalUserRowSelection}
                            setRowSelection={setInternalUserRowSelection}
                            setSortConfig={setSortConfig}
                            sortConfig={sortConfig}
                        />
                    </div>

                    <div className="hidden" role="table">
                        <div className="flex flex-col gap-y-12">
                            {openRestrictionList.map((restriction) => (
                                <MobileListItem
                                    key={restriction.restriction_id}
                                    isSelected={selectedIds
                                        .find(
                                            (singleId) =>
                                                singleId.restrictionId ===
                                                restriction.restriction_id
                                        )
                                        ?.clientIds.includes(
                                            restriction.client.client_id
                                        )}
                                    showCheckButton
                                    onClickCheckButton={() =>
                                        toggleItemSelection(
                                            restriction.restriction_id,
                                            restriction.client.client_id
                                        )
                                    }
                                    topChildren={
                                        <div className="flex items-center gap-x-8 text-base font-semibold capitalize">
                                            {`${restriction.client.first_name} ${restriction.client.last_name}`}
                                        </div>
                                    }
                                    bottomChildren={
                                        <div className="flex flex-col gap-y-8">
                                            <div className="w-full text-left">
                                                <span className="block font-medium text-xss uppercase">
                                                    Restricted user
                                                </span>
                                                <span className="block text-gray font-semibold text-xs capitalize">
                                                    {`${restriction.restricted_user.first_name} ${restriction.restricted_user.last_name}`}
                                                </span>
                                            </div>
                                            <div className="w-full text-left">
                                                <span className="block font-medium text-xss uppercase">
                                                    Date restricted
                                                </span>
                                                <span className="block text-gray font-semibold text-xs">
                                                    {formatDate(
                                                        restriction.date_restricted
                                                    )}
                                                </span>
                                            </div>
                                            <div className="w-full text-left">
                                                <span className="block font-medium text-xss uppercase">
                                                    Restricted by
                                                </span>
                                                <span className="block text-gray font-semibold text-xs capitalize">
                                                    {`${restriction.restricted_by.first_name} ${restriction.restricted_by.last_name}`}
                                                </span>
                                            </div>
                                        </div>
                                    }
                                />
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
